import {
  Button,
  Drawer,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import { Divider, IconButton } from 'utoniq-ui'

import {
  PaymentMethod,
  PrepaidPointsPaymentSourceFragment,
} from '@/clients/utoniq-core/schema'
import { Maybe } from '@/clients/utoniq-core/schema'
import { useForm, UseFormProps, useRouter } from '@/core'
import { useTranslation } from '@/core'
import { CellFrame, Organism } from '@/core/architecture'
import { OrganismFrame } from '@/core/architecture'
import { App } from '@/core/native'

import { CarrierPaymentOption } from '../../payment/CarrierPaymentOption'
import { CreditCardPayment } from '../../payment/CreditCardPayment'

type PrepaidPointChargeValues = {
  paymentMethod: PaymentMethod
}

type PaymentMethodSelectDrawerProps = {
  open: boolean
  paymentSource: Maybe<PrepaidPointsPaymentSourceFragment>
  chargeLoading: boolean
  /** アプリ毎に設定されているポイント購入のキャリア決済が有効かどうか */
  enablePointCarrierPayment: boolean
  /**
   * 親側で課金処理を走らせるコールバック。
   * 引数として選択された paymentMethod を渡す
   */
  onSubmit: (selectedPaymentMethod: PaymentMethod) => void
  onClose: () => void
}

/**
 * ポイントチャージ実行時に表示される
 * 支払い方法を選択するためのドロワー
 *
 * 「課金を確定する」ボタン押下時に
 * ユーザーが選択した paymentMethod を
 * onSubmit コールバックを通じて親コンポーネントに通知し責務を委譲する
 */
export const PaymentMethodSelectDrawer: Organism<PaymentMethodSelectDrawerProps> =
  ({
    open,
    paymentSource,
    chargeLoading,
    enablePointCarrierPayment,
    onSubmit,
    onClose,
  }) => {
    return (
      <Drawer open={open} onClose={onClose}>
        <PaymentMethodSelectDrawerHeader onClose={onClose} />
        <PaymentMethodSelectDrawerContent
          paymentSource={paymentSource}
          chargeLoading={chargeLoading}
          enablePointCarrierPayment={enablePointCarrierPayment}
          onSubmit={onSubmit}
        />
      </Drawer>
    )
  }

type PaymentMethodSelectDrawerHeaderProps = {
  onClose: () => void
}
const PaymentMethodSelectDrawerHeader: Organism<PaymentMethodSelectDrawerHeaderProps> =
  ({ onClose }) => {
    const { t } = useTranslation('prepaidPointCharge')
    return (
      <OrganismFrame>
        <Stack direction="row" sx={{ height: 55, px: 3 }} alignItems="center">
          <Typography variant="h6" align="center" width="100%">
            {t('confirmPayment')}
          </Typography>
          <IconButton.Close size="small" onClick={onClose} />
        </Stack>
      </OrganismFrame>
    )
  }

type PaymentMethodSelectDrawerContentProps = {
  paymentSource: Maybe<PrepaidPointsPaymentSourceFragment>
  chargeLoading: boolean
  enablePointCarrierPayment: boolean
  onSubmit: (selectedPaymentMethod: PaymentMethod) => void
  onNavigate?: () => Promise<void>
}
export const PaymentMethodSelectDrawerContent: Organism<PaymentMethodSelectDrawerContentProps> =
  ({
    paymentSource,
    chargeLoading,
    enablePointCarrierPayment,
    onSubmit,
    onNavigate,
  }) => {
    const { t } = useTranslation('prepaidPointCharge')
    const { routerPages } = useRouter()
    const isWeb = App.getPlatform() === 'web'

    const schema: UseFormProps['schema'] = o => ({
      paymentMethod: o.string(),
    })

    const { watch, setValue } = useForm<PrepaidPointChargeValues>({ schema })

    const paymentMethodWatch = watch('paymentMethod')

    // ── Drawer 内の「課金確定ボタン」押下時に呼ぶ関数 ──
    const submitHandler = async () => {
      if (!paymentMethodWatch) return

      // ── 親コンポーネントに支払い方法を渡して本来の課金ロジックを走らせる ──
      await onSubmit(paymentMethodWatch)
    }
    return (
      <>
        <RadioGroup
          value={paymentMethodWatch ?? ''}
          onChange={(_, value) => {
            setValue('paymentMethod', value as PaymentMethod, {
              shouldValidate: true,
            })
          }}
          sx={{ gap: theme => theme.spacing(4), px: 5 }}
        >
          <Typography variant="h5" color="text.secondary">
            {t('paymentMethod')}
          </Typography>
          <FormControlLabel
            value={PaymentMethod.CreditCard}
            checked={paymentMethodWatch === PaymentMethod.CreditCard}
            control={<Radio />}
            label={
              <CreditCardPayment
                paymentSource={paymentSource}
                goBackPath={routerPages.PrepaidPointCharge}
                onNavigate={onNavigate}
              />
            }
          />
          <Divider />

          {/**
           * ネイティブアプリでは escott でのキャリア決済を提供しないため
           * キャリア決済のオプションを非表示とする
           * ref. https://app.clickup.com/t/908127/UT-20067
           *
           * アプリ毎に設定されているポイント購入のキャリア決済が有効な場合のみ
           * キャリア決済の選択肢を表示する
           * ref. https://app.clickup.com/t/908127/UT-20671
           */}
          {isWeb && enablePointCarrierPayment && (
            <>
              <Stack>
                <Typography variant="h5">{t('carrierPayment')}</Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ pt: 2 }}
                >
                  {t('carrierPaymentDesc')}
                </Typography>
              </Stack>
              <Stack>
                <FormControlLabel
                  value={PaymentMethod.Au}
                  checked={paymentMethodWatch === PaymentMethod.Au}
                  control={<Radio />}
                  label={<CarrierPaymentOption carrier={t('au')} />}
                />
                <FormControlLabel
                  value={PaymentMethod.Softbank}
                  checked={paymentMethodWatch === PaymentMethod.Softbank}
                  control={<Radio />}
                  label={<CarrierPaymentOption carrier={t('softbank')} />}
                />
                <FormControlLabel
                  value={PaymentMethod.Docomo}
                  checked={paymentMethodWatch === PaymentMethod.Docomo}
                  control={<Radio />}
                  label={<CarrierPaymentOption carrier={t('docomo')} />}
                />
              </Stack>
              <Divider />
            </>
          )}
        </RadioGroup>
        <CellFrame
          sx={{
            py: 6,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Button
            size="large"
            sx={{ minWidth: 244 }}
            disabled={
              chargeLoading ||
              !paymentMethodWatch ||
              (paymentMethodWatch === PaymentMethod.CreditCard &&
                !paymentSource?.last4)
            }
            onClick={submitHandler}
            loading={chargeLoading}
          >
            {!paymentMethodWatch ||
            paymentMethodWatch === PaymentMethod.CreditCard
              ? t('toBuy')
              : t('confirmCarrierPayment')}
          </Button>
        </CellFrame>
      </>
    )
  }
